// Identity recovery currently supports Venezuelan documents only. Match the
// backend's key without changing the identification stored on the customer.
export function canonical_recovery_identification(value: string): string | null {
	const raw = value.trim().toUpperCase().replace(/[- ]/g, '');
	if (!/^[VE]?\d{6,9}$/.test(raw)) return null;
	return raw.replace(/^[VE]/, '').replace(/^0+/, '') || '0';
}

export function validate_email(email: string): boolean {
	email = email.trim();

	// Quick fail: empty or too long (RFC 3696 recommends max 320 chars)
	if (!email || email.length > 320) {
		return false;
	}

	// Robust regex for email validation
	// Source: Adapted from https://emailregex.com/ (a well-tested pattern)
	const emailRegex =
		/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

	return emailRegex.test(email);
}

export function validate_rif(rif: string, country: string) {
	if (typeof rif !== 'string' || !rif.length) return false;

	if (country === 'venezuela') return /^\d{8,10}$/.test(rif);

	return false;
}

export function validate_cedula(cedula: string, country: string) {
	if (typeof cedula !== 'string' || !cedula.length) return false;

	if (country === 'venezuela') return /^\d{7,8}$/.test(cedula);
	if (country === 'rd') return /^\d{11}$/.test(cedula);
	if (country === 'colombia') return /^\d{6,10}$/.test(cedula);
	if (country === 'bolivia') return /^\d{7,8}$/.test(cedula);
	if (country === 'peru') return validate_dni(cedula, country);

	return false;
}

export function validate_dni(dni: string, country: string) {
	if (typeof dni !== 'string' || !dni.length) return false;

	if (country === 'peru') return /^\d{8}$/.test(dni);

	return false;
}

export function validate_ruc(ruc: string, country: string) {
	if (typeof ruc !== 'string' || !ruc.length) return false;

	if (country === 'peru') return /^(10|15|20)\d{9}$/.test(ruc);

	return false;
}

export function validate_nit(nit: string, country: string) {
	if (typeof nit !== 'string' || !nit.length) return false;

	if (country === 'bolivia') return /^\d{7,10}$/.test(nit);
	if (country === 'colombia') return /^\d{7,10}$/.test(nit);

	return false;
}

export function validate_rnc(rnc: string, country: string) {
	if (typeof rnc !== 'string' || !rnc.length) return false;

	if (country === 'rd') return /^1\d{8}$/.test(rnc);

	return false;
}

export type IdentityType = 'rif' | 'rnc' | 'ruc' | 'cedula' | 'dni' | 'firma_personal' | 'extranjera' | 'passport' | 'nit';
export function validate_identity_number(identity_number: string, identity_type: IdentityType, country: string) {
	if (typeof identity_number !== 'string' || !identity_number.length || !identity_type || !country) return false;

	if (identity_type === 'rif') return validate_rif(identity_number, country);
	if (identity_type === 'firma_personal') return validate_rif(identity_number, country);
	if (identity_type === 'rnc') return validate_rnc(identity_number, country);
	if (identity_type === 'ruc') return validate_ruc(identity_number, country);
	if (identity_type === 'cedula') return validate_cedula(identity_number, country);
	if (identity_type === 'dni') return validate_dni(identity_number, country);
	if (identity_type === 'extranjera') return validate_cedula(identity_number, country);
	if (identity_type === 'passport') return typeof identity_number === 'string' && identity_number.trim().length > 3;
	if (identity_type === 'nit') return validate_nit(identity_number, country);
	return false;
}

export function validate_phone_number(phone_number: string, country: string) {
	if (typeof phone_number !== 'string' || !phone_number.length) return false;

	if (country === 'venezuela') return /^(0414|0424|0412|0416|0426|0422)\d{7}$/.test(phone_number);
	if (country === 'rd') return /^(809|829|849)\d{7}$/.test(phone_number);
	if (country === 'peru') return /^(9)\d{8}$/.test(phone_number);
	if (country === 'colombia') return /^(3)\d{9}$/.test(phone_number);
	if (country === 'bolivia') return /^(6|7)\d{7}$/.test(phone_number);

	return false;
}

export function validate_bank_account_number(bank_account_number: string, country: string, bank_name?: string) {
	if (typeof bank_account_number !== 'string' || !bank_account_number.length) return false;

	if (country === 'venezuela') return /^\d{20}$/.test(bank_account_number);
	if (country === 'peru') {
		if (bank_name && bank_name?.toLowerCase() === 'bbva') {
			return /^\d{18}$/.test(bank_account_number);
		} else if (bank_name && bank_name?.toLowerCase() === 'interbank') {
			return /^\d{13,14}$/.test(bank_account_number);
		} else if (bank_name && bank_name?.toLowerCase() === 'bcp') {
			return /^\d{13,14}$/.test(bank_account_number);
		} else if (bank_name && (bank_name?.toLowerCase() === 'scotiabanck' || bank_name?.toLowerCase() === 'scotiabank')) {
			return /^\d{10}$/.test(bank_account_number);
		}

		return /^\d{10,18}$/.test(bank_account_number);
	}
	if (country === 'rd') return /^\d{9,13}$/.test(bank_account_number);
	if (country === 'colombia') {
		if (bank_name && bank_name === 'Bancolombia') {
			return /^\d{11}$/.test(bank_account_number);
		} else if (bank_name && bank_name === 'Banco de Bogotá') {
			return /^\d{9,12}$/.test(bank_account_number);
		} else if (bank_name && bank_name === 'Davivienda') {
			return /^\d{10,12}$/.test(bank_account_number);
		} else if (bank_name && bank_name === 'BBVA Colombia') {
			return /^\d{11}$/.test(bank_account_number);
		} else if (bank_name && bank_name === 'Banco de Occidente') {
			return /^\d{9,13}$/.test(bank_account_number);
		}

		return /^\d{9,12}$/.test(bank_account_number);
	}
	if (country === 'bolivia') {
		if (bank_name && bank_name === 'Banco Económico') {
			return /^\d{13}$/.test(bank_account_number);
		} else {
			return /^\d{6,20}$/.test(bank_account_number);
		}
	}

	return false;
}

export function validate_interbank_code(interbank_code: string, country: string) {
	if (typeof interbank_code !== 'string' || !interbank_code.length) return false;

	if (country === 'peru') return /^\d{18}$|^\d{20}$/.test(interbank_code);
	if (country === 'bolivia') return /^\d{18}$|^\d{20}$/.test(interbank_code);

	return false;
}

export const validate_account_holder_name = (name: string): boolean => {
	const trimmed_name = name.trim();
	if (trimmed_name.length < 2 || trimmed_name.length > 120) return false;
	const name_regex = /^[\p{L}\p{M}\s'-]+$/u;
	if (!name_regex.test(trimmed_name)) return false;
	const repeated_separator_regex = /[ '\-]{2,}/;
	if (repeated_separator_regex.test(trimmed_name)) return false;

	return true;
};

export const numbers_only = /[^0-9]/g;
export const numbers_dot = /[^0-9.]/g;
export const alphanumeric = /[^a-zA-Z0-9]/g;
export const numbers_letters_dot_comma = /[^a-zA-Z0-9.,\sáéíóúüÁÉÍÓÚÜÑñ]/g;
